import React from 'react';
import { useConsulta } from './sanity';

/**
 * Textos de las páginas: un documento único por página en Sanity ("Páginas" en el
 * panel, studio/schemaTypes/paginas/). El respaldo es el JSON de
 * src/Data/Paginas/<id>.json, que es también lo que `npm run textos` sube al
 * panel la primera vez: lo que se ve sin Sanity y lo que el equipo encuentra
 * para editar son lo mismo.
 */

const esObjeto = (v) => v !== null && typeof v === 'object' && !Array.isArray(v);

// Cada texto del panel llega como { texto, elemento }: el texto se entrega como
// string (así los componentes no cambian) y el elemento HTML que eligió el
// equipo queda guardado aparte, por objeto y campo, para `elemento()`
const ELEGIDOS = new WeakMap();
const CLAVES_TEXTO = ['texto', 'elemento', '_type', '_key'];
const esTextoConElemento = (v) =>
    esObjeto(v) && ('texto' in v || 'elemento' in v) && Object.keys(v).every((k) => CLAVES_TEXTO.includes(k));

function anotar(objeto, campo, valor) {
    if (!valor?.elemento) return;
    if (!ELEGIDOS.has(objeto)) ELEGIDOS.set(objeto, {});
    ELEGIDOS.get(objeto)[campo] = valor.elemento;
}

// Un ítem de lista: sus textos pasan a string y se anotan sus elementos
function itemDeLista(item) {
    if (!esObjeto(item)) return item;
    const fuera = { ...item };
    Object.entries(item).forEach(([clave, valor]) => {
        if (esTextoConElemento(valor)) fuera[clave] = valor.texto ?? '';
    });
    Object.entries(item).forEach(([clave, valor]) => {
        if (esTextoConElemento(valor)) anotar(fuera, clave, valor);
    });
    return fuera;
}

/**
 * Lo de Sanity encima del respaldo, campo por campo: un campo vacío (o una lista
 * vacía) cae al texto del JSON. Las listas no se mezclan ítem por ítem: si el
 * equipo reordena o borra uno, se respeta tal cual.
 */
export function combinar(porDefecto, datos) {
    if (Array.isArray(porDefecto)) return Array.isArray(datos) && datos.length ? datos.map(itemDeLista) : porDefecto;
    if (esObjeto(porDefecto)) {
        const d = esObjeto(datos) ? datos : {};
        const fuera = Object.fromEntries(Object.keys(porDefecto).map((clave) => [clave, combinar(porDefecto[clave], d[clave])]));
        Object.keys(porDefecto).forEach((clave) => {
            if (esTextoConElemento(d[clave])) anotar(fuera, clave, d[clave]);
        });
        return fuera;
    }
    if (esTextoConElemento(datos)) return combinar(porDefecto, datos.texto);
    if (typeof datos === 'string') return datos.trim() ? datos : porDefecto;
    return datos ?? porDefecto;
}

const VALIDOS = ['h1', 'h2', 'h3', 'h4', 'h5', 'h6', 'p', 'span', 'div'];

/**
 * El elemento HTML de un texto: el que eligió el equipo en el panel o, si no
 * eligió, el del diseño. `const Titulo = elemento(textos, 'titular', 'h2')` y
 * después `<Titulo className=…>`. Solo cambia la semántica (Google, lectores
 * de pantalla): el tamaño y la letra los siguen poniendo las clases.
 */
export function elemento(objeto, campo, porDefecto) {
    // En la vista previa el valor podría traer los caracteres invisibles del stega
    const elegido = String((esObjeto(objeto) && ELEGIDOS.get(objeto)?.[campo]) || '').replace(/[^a-z0-9]/g, '');
    return VALIDOS.includes(elegido) ? elegido : porDefecto;
}

const CONSULTA = '*[_id == $id][0]';

/** Textos de una página con su respaldo: `useTextos('paginaNosotros', respaldo)`. */
export function useTextos(id, porDefecto) {
    const { datos } = useConsulta(CONSULTA, { id }, null);
    return combinar(porDefecto, datos);
}

/**
 * "Somos un equipo *resolutivo y dinámico.*" → partes con `accent` para las
 * palabras entre asteriscos, que van en serif itálica (el formato de ScrollWords).
 * En la vista previa el texto trae al final caracteres invisibles (stega) que
 * dicen de qué campo viene: quedan en la última parte y no molestan.
 */
export function partes(texto = '') {
    return String(texto)
        .split('*')
        .map((t, i) => ({ text: t.replace(/\s+/g, ' ').trim(), accent: i % 2 === 1 }))
        .filter((p) => p.text);
}

/** Palabra por palabra, para los titulares que animan cada una: [{ t, acento }]. */
export const palabras = (texto) =>
    partes(texto).flatMap((p) => p.text.split(' ').map((t) => ({ t, acento: p.accent })));

/** El texto con las palabras entre asteriscos en serif itálica. */
export function ConAcento({ texto, clase = 'font-serif font-normal italic' }) {
    // Sin recortar: los espacios alrededor de los asteriscos son los que escribió el equipo
    return String(texto ?? '')
        .replace(/\s+/g, ' ')
        .split('*')
        .map((t, i) => (i % 2 ? <span key={i} className={clase}>{t}</span> : t));
}

/** El texto sin asteriscos (para `alt`, `aria-label` y lo que no lleva estilo). */
export const sinAcento = (texto = '') => String(texto).replace(/\*/g, '');
